import React, { useState, useEffect, useRef } from 'react';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import About from './components/About';
import Projects from './components/Projects';
import Skills from './components/Skills';
import Contact from './components/Contact';
import Footer from './components/Footer';
import useIntersectionObserver from './hooks/useIntersectionObserver';
import './App.css';

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Intersection Observer for animations
  const [observe, entries] = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  });
  
  const projectRefs = useRef([]);
  const skillRefs = useRef([]);
  const aboutRef = useRef(null);
  const contactRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Setup intersection observers
  useEffect(() => {
    // Observe project cards
    projectRefs.current.forEach(ref => {
      if (ref) observe(ref);
    });
    
    // Observe skill categories
    skillRefs.current.forEach(ref => {
      if (ref) observe(ref);
    });
    
    // Observe other sections
    if (aboutRef.current) observe(aboutRef.current);
    if (contactRef.current) observe(contactRef.current);
  }, [observe]);
  
  // Handle intersection animations
  useEffect(() => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-in');
      }
    });
  }, [entries]);

  // Easter egg: Konami code
  useEffect(() => {
    let konamiCode = [];
    const konamiPattern = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];

    const handleKeyDown = (e) => {
      konamiCode.push(e.key);
      konamiCode = konamiCode.slice(-10);
      
      if (konamiCode.join(',') === konamiPattern.join(',')) {
        document.body.style.animation = 'rainbow 2s linear infinite';
        showNotification('🌈 You found the easter egg!', 'success');
        
        setTimeout(() => {
          document.body.style.animation = '';
        }, 5000);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  return (
    <div className="App">
      <Navigation scrolled={scrolled} />
      <Hero />
      <About ref={aboutRef} />
      <Projects projectRefs={projectRefs} />
      <Skills skillRefs={skillRefs} />
      <Contact ref={contactRef} showNotification={showNotification} />
      <Footer />
      
      {notification && (
        <div className={`notification notification-${notification.type} show`}>
          {notification.message}
        </div>
      )}
    </div>
  );
}

export default App;
